import { createContextualCan } from '@casl/react'
import { Ability } from '@casl/ability'
import { computed, IReactionDisposer, observable, when } from 'mobx'
import { Container, Singleton } from 'typescript-ioc/es5'
import { AbilityAction } from '../enums/ability-action.enum'
import { SubjectEntity } from '../enums/ability-entity.enum'
import type { AppState } from './app'
import AccessControlService from '../services/access-control.service'
import { SessionState } from '@pushly/aqe'
import { DomainDto } from '../dtos/domain'
import { AccountDto } from '../dtos/account.dto'
import { UserDto } from '../dtos/user'
import { SegmentModel } from '../models/segments/segment.model'
import {
    addVisibilityAwareIntervalRunner,
    LastRunTimestampUpdater,
    VisibilityAwareIntervalCleaner,
} from '../_utils/visibility-api'
import { createContext } from 'react'

const ABILITY_REFRESH_INTERVAL = 300000

const emptyAbilitySet: AppAbility = new Ability()

// TODO-RBAC
//  - remove helper once all entities have valid dtos/models
export interface ICaslSubject<T extends SubjectEntity> {
    __caslSubjectType__: T | string
}

export type AppAbility = Ability<[AbilityAction, any]>

type KnownIdentity = DomainDto | AccountDto | UserDto | SegmentModel
export const asCaslSubject = <T extends SubjectEntity>(type: T, conditions: any): ICaslSubject<T> =>
    ({
        __caslSubjectType__: type,
        ...conditions,
    }) as any

/**
 * Demo attributes the server evaluates for domain-owned content. A condition on a field the
 * subject lacks never matches. `isDemo` and `accountIsDemo` are grant conditions on true, so only an
 * explicit true counts. `isDemoTemplate` is a grant condition on false (demo content) and on true
 * (template content), so it is set only when the flag is a boolean and left off otherwise: a missing
 * flag must match neither.
 */
type DemoAttributeSource = Pick<DomainDto, 'isDemo' | 'accountIsDemo' | 'isDemoTemplate'>
type DemoAttributes = { isDemo: boolean; accountIsDemo: boolean; isDemoTemplate?: boolean }

const explicitBoolean = (flag: unknown): boolean | undefined => (typeof flag === 'boolean' ? flag : undefined)

const getDemoAttributes = (domain?: DemoAttributeSource): DemoAttributes => ({
    isDemo: domain?.isDemo === true,
    accountIsDemo: domain?.accountIsDemo === true,
    isDemoTemplate: explicitBoolean(domain?.isDemoTemplate),
})

/**
 * Demo attributes for a subject that carries its own domain id (a list's `domainId` prop, a
 * segment record), to be spread into that subject alongside its own fields.
 *
 * Given a domain, they are that domain's own. Given an id, they are the current domain's when the
 * id is the current domain's, else the matching record in `knownDomains` (the user's domain list).
 * A domain the app holds no attributes for reads as false and the check fails closed.
 */
export const getDemoAttributesFor = (
    domain: DemoAttributeSource | number | string | undefined,
    currentDomain: (DemoAttributeSource & { id?: number }) | undefined,
    knownDomains?: (DemoAttributeSource & { id?: number })[],
): DemoAttributes => {
    if (domain !== null && typeof domain === 'object') {
        return getDemoAttributes(domain)
    }

    if (domain === undefined) {
        return getDemoAttributes(undefined)
    }

    if (currentDomain?.id !== undefined && String(currentDomain.id) === String(domain)) {
        return getDemoAttributes(currentDomain)
    }

    // Any other domain is known only through the user's domain list, and a domain missing from
    // it reads as false.
    return getDemoAttributes(knownDomains?.find((d) => d.id !== undefined && String(d.id) === String(domain)))
}

/**
 * Demo attributes the server evaluates for org-owned content (notifications and segments that
 * span an org's domains). Grants for demo orgs are conditioned on the org's own two flags, which
 * a domain carries as `accountIs*`. The same rules as the domain attributes apply: `isDemo` counts only
 * on an explicit true, and `isDemoTemplate` is left off unless it is a boolean.
 */
type OrgDemoAttributeSource = Pick<DomainDto, 'accountIsDemo' | 'accountIsDemoTemplate'>
type OrgDemoAttributes = { isDemo: boolean; isDemoTemplate?: boolean }

const getOrgDemoAttributes = (domain?: OrgDemoAttributeSource): OrgDemoAttributes => ({
    isDemo: domain?.accountIsDemo === true,
    isDemoTemplate: explicitBoolean(domain?.accountIsDemoTemplate),
})

/**
 * Demo attributes of the org with the given id, for a subject that names its org. The app holds
 * an org's flags only through the current domain, so an org that is not the current domain's
 * reads as false and the check fails closed.
 */
export const getOrgDemoAttributesFor = (
    orgId: number | string | undefined,
    currentDomain: (OrgDemoAttributeSource & { accountId?: number }) | undefined,
): OrgDemoAttributes => {
    const isCurrentOrg =
        orgId !== undefined &&
        currentDomain?.accountId !== undefined &&
        String(currentDomain.accountId) === String(orgId)

    return getOrgDemoAttributes(isCurrentOrg ? currentDomain : undefined)
}

@Singleton
export class AbilityStore {
    public acs: AccessControlService

    protected abilitiesLoader: Promise<void> | undefined
    protected abilitiesLoaded: boolean = false
    protected autoRefreshHandler:
        | {
              cleanup: VisibilityAwareIntervalCleaner
              updateLastRunTimestamp: LastRunTimestampUpdater
          }
        | undefined

    @observable
    protected _abilities: AppAbility = emptyAbilitySet

    protected disposers: {
        load?: IReactionDisposer
        flush?: IReactionDisposer
    } = {}

    public constructor(protected readonly appState: AppState) {
        this.setupEventWatchers()
        this.loadAbilitiesForCurrentUser()
    }

    @computed
    public get abilities() {
        return this._abilities
    }

    /**
     * generates basic AbilityIdentity for the currently active domain's org
     */
    public get currentOrgIdentity(): ICaslSubject<SubjectEntity.ORG> {
        const domain = this.appState.currentDomain

        return asCaslSubject(SubjectEntity.ORG, {
            id: domain?.accountId,
            statusId: domain?.statusId,
            // Grants for demo organizations match on the organization's own demo flags, which
            // the domain carries as `accountIs*`. Each is true only on an explicit true.
            ...getOrgDemoAttributes(domain),
        })
    }

    /**
     * generates basic AbilityIdentity for the currently active domain
     */
    public get currentDomainIdentity(): ICaslSubject<SubjectEntity.DOMAIN> {
        const domain = this.appState.currentDomain

        return asCaslSubject(SubjectEntity.DOMAIN, {
            id: domain?.id,
            statusId: domain?.statusId,
            ...getDemoAttributes(domain),
        })
    }

    /**
     * generates basic AbilityIdentity for the currently active user
     */
    public get currentUserIdentity(): ICaslSubject<SubjectEntity.USER> {
        return asCaslSubject(SubjectEntity.USER, {
            id: this.appState.currentUser!.id,
        })
    }

    /**
     * passthrough to @casl/ability can(...)
     */
    public can<T extends SubjectEntity>(
        action: AbilityAction,
        subject: T | ICaslSubject<T> | KnownIdentity,
        field?: string,
    ): boolean {
        return this._abilities.can(action, subject, field)
    }

    /**
     * passthrough to @casl/ability cannot(...)
     */
    public cannot<T extends SubjectEntity>(
        action: AbilityAction,
        subject: T | ICaslSubject<T> | KnownIdentity,
        field?: string,
    ): boolean {
        return this._abilities.cannot(action, subject, field)
    }

    /**
     * generates basic AbilityIdentity for given entity type
     * using the currently active domain's org id
     */
    public getOrgOwnedIdentityFor<T extends SubjectEntity>(type: T) {
        const domain = this.appState.currentDomain!

        return asCaslSubject<T>(type, { accountId: domain.accountId, ...getOrgDemoAttributes(domain) })
    }

    /**
     * generates basic AbilityIdentity for given entity type
     * using the currently active domain's id
     */
    public getDomainOwnedIdentityFor<T extends SubjectEntity>(type: T) {
        const domain = this.appState.currentDomain

        return asCaslSubject<T>(type, { domainId: domain?.id, ...getDemoAttributes(domain) })
    }

    /**
     * force refresh abilities from api layer
     */
    public async refresh() {
        this._abilities = await this.acs.getAbilities(true)

        try {
            await this.acs.getUserRecords(true)
        } catch (err) {
            if (process.env.IS_LOCAL) {
                console.debug('ability_store_records_refresh_error', err)
            }
        }

        this.autoRefreshHandler?.updateLastRunTimestamp()
    }

    /**
     * Removes abilities and resets state.
     * should only be used when user is logged out
     * of the application.
     */
    public flush = () => {
        if (this.abilitiesLoaded) {
            // reset abilities and loaded state
            this.abilitiesLoaded = false
            this._abilities = emptyAbilitySet

            // cleanup and remove visibility auto refresh handler
            this.autoRefreshHandler?.cleanup()
            this.autoRefreshHandler = undefined

            // reset load event watcher
            this.setupEventWatchers()
        }
    }

    /**
     * Waits for current user to be loaded.
     * Once available permissions are requested
     * and loaded into the current scope.
     */
    public async loadAbilitiesForCurrentUser() {
        if (this.appState.currentUser && !this.abilitiesLoaded) {
            this.abilitiesLoader = new Promise(async (res) => {
                this.abilitiesLoaded = true
                /**
                 * modifying the load count before login will
                 * result in a brief UI flash.
                 */
                let shouldAlterAppLoadingCount = Container.get(SessionState).isAuthenticated

                try {
                    if (shouldAlterAppLoadingCount) {
                        this.appState.loadingCount++
                    }

                    this.acs = AccessControlService.forUser(this.appState.currentUser!)
                    this._abilities = await this.acs.getAbilities()

                    try {
                        await this.acs.getUserRecords()
                    } catch (err) {
                        if (process.env.IS_LOCAL) {
                            console.debug('ability_store_records_load_error', err)
                        }
                    }

                    // stop watching for load event
                    this.disposers.load?.()
                } catch (err) {
                    this.abilitiesLoaded = false

                    if (process.env.IS_LOCAL) {
                        console.debug('ability_store_error', err)
                    }
                } finally {
                    if (shouldAlterAppLoadingCount) {
                        this.appState.loadingCount--
                    }

                    res()

                    this.abilitiesLoader = undefined
                }
            })
        }

        return this.abilitiesLoader
    }

    protected setupEventWatchers() {
        this.disposers.load = when(
            () => !!this.appState.currentUser?.id,
            async () => {
                await this.loadAbilitiesForCurrentUser()
                this.initializeAbilitiesRefresher()
            },
        )
    }

    protected initializeAbilitiesRefresher() {
        if (!this.autoRefreshHandler) {
            const [cleanup, updateTS] = addVisibilityAwareIntervalRunner(
                'user-permissions',
                async () => this.refresh(),
                ABILITY_REFRESH_INTERVAL,
                true,
            )

            this.autoRefreshHandler = {
                cleanup,
                updateLastRunTimestamp: updateTS,
            }
        }
    }
}

/**
 * bound fn component for @casl/react <Can ... />
 * using the injected abilities
 */

export const AbilityContext = createContext({})
export const CurrentUserCan = createContextualCan(AbilityContext.Consumer)
